"use client"

import { useEffect, useState } from "react"
import { getDatabase, ref, get, update, push, onValue } from "firebase/database"
import { getAuth } from "firebase/auth"
import { format, toZonedTime } from "date-fns-tz"
import { useAccount } from "../context/AccountContext"
import { useGetCurrentUserId } from "../hooks/useGetCurrentUserId"

import "./UserStockStatus.css"
import {
  FaInfoCircle,
  FaChevronDown,
  FaChartBar,
  FaBatteryFull,
  FaBatteryHalf,
  FaBatteryEmpty,
  FaHistory,
  FaEdit,
  FaClipboardList,
  FaUserTie,
} from "react-icons/fa"
import { MdInfo } from "react-icons/md"

const UserStockStatus = ({ targetUserId, partnerName = "Unknown Partner" }) => {
 const { currentAccount, loading: accountLoading } = useAccount()
  const { userId: hookUserId, isPartner, loading: partnerLoading } = useGetCurrentUserId()

  // === IMPORTANT: Same logic as Downloads ===
  const effectiveUserId = targetUserId || hookUserId
  const [mainUserId, setMainUserId] = useState(null)
  const [codes, setCodes] = useState({})
  const [loading, setLoading] = useState(true)
  const [companyName, setCompanyName] = useState("")
  // const [ setPartnerName] = useState("")
  const [activeTab, setActiveTab] = useState("amend")
  const [showSetCapacityModal, setShowSetCapacityModal] = useState(false);
  const [tempCapacity, setTempCapacity] = useState("");
  const [amendLivePercentage, setAmendLivePercentage] = useState(null)
  // Add this new state near other states
  const [hasAutoSelected, setHasAutoSelected] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false)
  const [assigningToSite, setAssigningToSite] = useState(null)
  const [partners, setPartners] = useState([])
  const [selectedPartner, setSelectedPartner] = useState("")
  const [sitePartnerAssignments, setSitePartnerAssignments] = useState({})
  const [currentStockError, setCurrentStockError] = useState("");
  // Report Tab
  const [selected, setSelected] = useState({ codeId: "", siteId: "", unit: "MT" })
  const [stockCapacity, setStockCapacity] = useState("")
  const [currentStock, setCurrentStock] = useState("")
  const [updating, setUpdating] = useState(false)
  const [livePercentage, setLivePercentage] = useState(null)
  const [showReportConfirm, setShowReportConfirm] = useState(false)

  // Amend Tab
  const [amendSelected, setAmendSelected] = useState({ codeId: "", siteId: "", unit: "MT" })
  const [amendCapacity, setAmendCapacity] = useState("")
  const [amendCurrent, setAmendCurrent] = useState("")
  const [amending, setAmending] = useState(false)
  const [showAmendConfirm, setShowAmendConfirm] = useState(false)

  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [amendDropdownOpen, setAmendDropdownOpen] = useState(false)

  const gifUrl = "/Images/New notification.gif"
  const auth = getAuth()
  const db = getDatabase()
  const MAX = 100000000;
  // ──────────────────────────────
  // 1. Set mainUserId from prop (account switch)
  // ──────────────────────────────
  useEffect(() => {
    if (targetUserId) {
      setMainUserId(targetUserId)
      setLoading(false)
    }
  }, [targetUserId])

  // ──────────────────────────────
  // 2. Determine mainUserId + load partners & assignments (fallback path)
  // ──────────────────────────────
 useEffect(() => {
  if (accountLoading || partnerLoading || !effectiveUserId) return;

  const determineMainAndPartner = async () => {
    let mainId = effectiveUserId;
    let partner = isPartner;
    let pName = "Partner";

    if (isPartner) {
      const partnerSnap = await get(ref(db, `profileDashboard/${auth.currentUser.uid}`));
      if (partnerSnap.exists()) {
        const partnerData = partnerSnap.val();
        mainId = partnerData.ownerId;           // ← Important
        pName = partnerData.name || "Partner";
      }
    } 
    // NEW: If it's a firm, get the real main owner
    else if (currentAccount?.mainUid || currentAccount?.parentUid) {
      mainId = currentAccount.mainUid || currentAccount.parentUid;
    }

    setMainUserId(mainId);
    if (partner) setActiveTab("amend");

    // Load partners using the REAL main ID
    if (!partner && mainId) {
      const partnersSnap = await get(ref(db, `profileDashboard/${mainId}/partners`));
      
      if (partnersSnap.exists()) {
        const partnerEntries = Object.keys(partnersSnap.val()).filter(uid => 
          partnersSnap.val()[uid] === true
        );

        const list = await Promise.all(
          partnerEntries.map(async (id) => {
            const snap = await get(ref(db, `profileDashboard/${id}`));
            return {
              id,
              name: snap.exists() ? snap.val().name || "Unknown Partner" : "Unknown Partner"
            };
          })
        );

        setPartners(list);
      } else {
        setPartners([]); // Clear when no partners
      }
    }
  };

  determineMainAndPartner();
}, [currentAccount, effectiveUserId, isPartner, accountLoading, partnerLoading]);
// Add this useEffect
useEffect(() => {
  if (!mainUserId || isPartner) {
    setPartners([]);
    return;
  }

  const partnersRef = ref(db, `profileDashboard/${mainUserId}/partners`);
  
  const unsubscribe = onValue(partnersRef, async (snap) => {
    if (!snap.exists()) {
      setPartners([]);
      return;
    }

    const partnerFlags = snap.val();
    const partnerUids = Object.keys(partnerFlags).filter(uid => partnerFlags[uid] === true);

    const list = await Promise.all(
      partnerUids.map(async (id) => {
        const userSnap = await get(ref(db, `profileDashboard/${id}`));
        return {
          id,
          name: userSnap.exists() ? userSnap.val().name || "Unknown Partner" : "Unknown Partner"
        };
      })
    );

    setPartners(list);
  });

  return () => unsubscribe();
}, [mainUserId, isPartner]);

  useEffect(() => {
    if (accountLoading || partnerLoading || !effectiveUserId) return

    setMainUserId(effectiveUserId)

    if (isPartner) {
      setActiveTab("amend")
    }
  }, [effectiveUserId, isPartner, accountLoading, partnerLoading])

  // ──────────────────────────────
  // 3. Load company name
  // ──────────────────────────────
 // Load company name
  useEffect(() => {
    if (!effectiveUserId) return
    const fetchName = async () => {
      const snap = await get(ref(db, `profileDashboard/${effectiveUserId}/company_name`))
      if (snap.exists()) setCompanyName(snap.val())
    }
    fetchName()
  }, [effectiveUserId])

  // Real-time listeners - Use effectiveUserId (Critical Fix)
  useEffect(() => {
    if (!effectiveUserId) return

    const codesRef = ref(db, `new-code/${effectiveUserId}/codes`)
    const assignRef = ref(db, `site_partner_assignments/${effectiveUserId}`)

    get(assignRef).then(snap => setSitePartnerAssignments(snap.val() || {}))

    const unsubCodes = onValue(codesRef, (snap) => {
      setCodes(snap.val() || {})
      setLoading(false)
    })

    const unsubAssign = onValue(assignRef, (snap) => {
      setSitePartnerAssignments(snap.val() || {})
    })

    return () => {
      unsubCodes()
      unsubAssign()
    }
  }, [effectiveUserId])



  // ──────────────────────────────
  // 5. Live percentage calculators
  // ──────────────────────────────
  useEffect(() => {
    if (stockCapacity && currentStock) {
      const cap = parseFloat(stockCapacity)
      const cur = parseFloat(currentStock)
      if (!isNaN(cap) && !isNaN(cur) && cap > 0) {
        setLivePercentage(((cur / cap) * 100).toFixed(1))
      } else {
        setLivePercentage(null)
      }
    } else {
      setLivePercentage(null)
    }
  }, [stockCapacity, currentStock])

  useEffect(() => {
    if (amendCapacity && amendCurrent) {
      const cap = parseFloat(amendCapacity)
      const cur = parseFloat(amendCurrent)
      if (!isNaN(cap) && !isNaN(cur) && cap > 0) {
        setAmendLivePercentage(((cur / cap) * 100).toFixed(1))
      } else {
        setAmendLivePercentage(null)
      }
    } else {
      setAmendLivePercentage(null)
    }
  }, [amendCapacity, amendCurrent])

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest(".stockstatus_dropdown_group")) {
        setIsDropdownOpen(false)
        setAmendDropdownOpen(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  // Reset form when mainUserId changes
  useEffect(() => {
    setSelected({ codeId: "", siteId: "", unit: "MT" })
    setAmendSelected({ codeId: "", siteId: "", unit: "MT" })
    setStockCapacity("")
    setCurrentStock("")
    setAmendCapacity("")
    setAmendCurrent("")
    setLivePercentage(null)
    setAmendLivePercentage(null)
  }, [mainUserId])

  // ──────────────────────────────
  // Helper Functions
  // ──────────────────────────────
  const getTimestampMs = (ts) => (!ts ? 0 : new Date(ts).getTime())
  const getIndianTime = (ts) => (!ts ? "—" : format(toZonedTime(new Date(ts), "Asia/Kolkata"), "dd MMM yyyy, hh:mm a"))

  const getFillClass = (pct) => {
    if (!pct) return ""
    if (pct > 60) return "green"
    if (pct >= 30) return "yellow"
    return "red"
  }

  const getMessage = (pct) => {
    if (!pct) return { icon: <FaChartBar />, msg: "Enter values to see status" }
    if (pct > 60) return { icon: <FaBatteryFull />, msg: `${pct}% (Full)` }
    if (pct >= 30) return { icon: <FaBatteryHalf />, msg: `${pct}% (Medium)` }
    return { icon: <FaBatteryEmpty />, msg: `${pct}% (Low)` }
  }

  const formatFullAddress = (site) => {
  if (!site || !site.address) return "Unknown Site";
  
  const base = site.address || "Site";
  const parts = [site.city, site.state, site.pincode].filter(Boolean);
  return parts.length ? `${base} — ${parts.join(", ")}` : base;
};

 const getFilteredSites = () => {
    if (!codes || !effectiveUserId) return []
    const allSites = Object.entries(codes).flatMap(([cId, c]) =>
      Object.entries(c.sites || {}).map(([sId, s]) => ({
        codeId: cId,
        siteId: sId,
        ...s,
      }))
    )

    if (isPartner) {
      return allSites.filter(site => {
        const key = `${site.codeId}_${site.siteId}`
        return sitePartnerAssignments[key]?.partnerId === auth.currentUser.uid
      })
    }
    return allSites
  }

  // ──────────────────────────────
  // Notifications & Actions
  // ──────────────────────────────
  const sendUserNotification = async (address, cap, cur, pct, status, action) => {
    if (!mainUserId || !isPartner) return
    const notifRef = ref(db, `user_notifications/StockStatus/${effectiveUserId}`)
    await push(notifRef, {
      codeId: amendSelected.codeId,
      title: `Partner Updated Stock`,
      message: `Your Partner *${partnerName}* has ${action} stock at *${address}* → Capacity ${cap}, Current ${cur} (${pct}% - ${status})`,
      gifUrl,
      timestamp: new Date().toISOString(),
      seen: false,
      updatedBy: partnerName,
      siteAddress: address,
      percentage: pct,
      status,
    })
  }

  const handleAssignPartner = async () => {
    if (!selectedPartner || !assigningToSite) return alert("Select a partner")

    try {
      const siteKey = `${assigningToSite.codeId}_${assigningToSite.siteId}`
      const assignRef = ref(db, `site_partner_assignments/${mainUserId}/${siteKey}`) // ← mainUserId = current firm or main

      await update(assignRef, {
        partnerId: selectedPartner,
        partnerName: partners.find(p => p.id === selectedPartner)?.name || "Unknown",
        siteAddress: formatFullAddress(assigningToSite),
        city: assigningToSite.city || null,
        state: assigningToSite.state || null,
        pincode: assigningToSite.pincode || null,
        assignedOn: new Date().toISOString(),
      })

      alert("Partner assigned successfully!")
      setShowAssignModal(false)
      setAssigningToSite(null)
      setSelectedPartner("")
    } catch (e) {
      console.error(e)
      alert("Failed to assign partner.")
    }
  }

  const handleSaveStock = async () => {
    if (!amendCapacity || !amendCurrent) return alert("Please fill both Capacity and Current Stock")

    const cap = parseFloat(amendCapacity)
    const cur = parseFloat(amendCurrent)
    if (isNaN(cap) || isNaN(cur) || cap <= 0) return alert("Invalid values")

    const siteKey = `${amendSelected.codeId}_${amendSelected.siteId}`
    const siteRef = ref(db, `new-code/${mainUserId}/codes/${amendSelected.codeId}/sites/${amendSelected.siteId}`)
    const site = codes[amendSelected.codeId]?.sites?.[amendSelected.siteId] || {}

    const isFirstTime = !site.stockCapacity

    const pct = ((cur / cap) * 100).toFixed(2)
    const status = pct > 60 ? "High" : pct >= 30 ? "Medium" : "Low"

    const updateData = {
      stockStatus: status,
      stockCapacity: cap,
      currentStock: cur,
      stockPercentage: pct,
      stockUnit: amendSelected.unit,
      updatedOn: new Date().toISOString(),
      updatedBy: effectiveUserId,
    }

    // Only for updates (not first time)
    if (!isFirstTime) {
      updateData.amended = true
      updateData.previous = {
        currentStock: site.currentStock,
        stockCapacity: site.stockCapacity,
      }
    }

    setAmending(true)

    try {
      await update(siteRef, updateData)

      // Partner notification (only on update)
      if (isPartner && !isFirstTime) {
        await sendUserNotification(
          site.address,
          cap,
          cur,
          pct,
          status,
          "updated"
        )
      }

      // Low/Medium alert to supplier
      if (status !== "High") {
        const notifRef = ref(db, `supplier_notifications/StockStatus/${mainUserId}`)
        await push(notifRef, {
          codeId: amendSelected.codeId,
          title: isFirstTime ? "New Stock Reported" : `${status} Stock Alert`,
          message: `${companyName} — Stock at ${site.address || "selected site"} is ${isFirstTime ? "now reported" : `*${status}*`}. ${isFirstTime ? "Initial entry completed." : "Please contact for reorder."}`,
          gifUrl,
          timestamp: new Date().toISOString(),
          seen: false,
        })
      }

      alert(isFirstTime ? "Stock successfully added!" : "Stock updated successfully!")

      // Reset form
      setAmendCapacity("")
      setAmendCurrent("")
      setAmendSelected({ codeId: "", siteId: "", unit: "MT" })
    } catch (err) {
      console.error(err)
      alert("Failed to save stock.")
    } finally {
      setAmending(false)
    }
  }

// 1. Reset when mainUserId changes
useEffect(() => {
  setHasAutoSelected(false);
  setAmendSelected({ codeId: "", siteId: "", unit: "MT" });
  setAmendCapacity("");
  setAmendCurrent("");
  setAmendLivePercentage(null);
}, [mainUserId]);

// 2. Auto-select logic - Improved with safety checks
useEffect(() => {
  if (
    activeTab !== "amend" || 
    loading || 
    !effectiveUserId || 
    hasAutoSelected || 
    amendSelected.siteId || 
    !codes || 
    Object.keys(codes).length === 0
  ) return;

  const sites = getFilteredSites();

  if (sites.length === 0) return;

  // Prefer site that already has stock data
  let firstChoice = sites.find(site => site.stockCapacity) || sites[0];

  setAmendSelected({
    codeId: firstChoice.codeId,
    siteId: firstChoice.siteId,
    unit: firstChoice.stockUnit || "MT",
  });

  setAmendCapacity(firstChoice.stockCapacity || "");
  setAmendCurrent(firstChoice.currentStock || "");

  setHasAutoSelected(true);

}, [activeTab, loading, effectiveUserId, codes, sitePartnerAssignments, hasAutoSelected, isPartner]);

// 3. Re-validate selected site when codes update (Important Fix)
useEffect(() => {
  if (!amendSelected.siteId || !codes) return;

  const siteExists = codes[amendSelected.codeId]?.sites?.[amendSelected.siteId];

  if (!siteExists) {
    // Selected site is no longer valid (after account switch) → reset
    setAmendSelected({ codeId: "", siteId: "", unit: "MT" });
    setAmendCapacity("");
    setAmendCurrent("");
    setHasAutoSelected(false);
  } else {
    // Update values in case they changed
    setAmendCapacity(siteExists.stockCapacity || "");
    setAmendCurrent(siteExists.currentStock || "");
  }
}, [codes, amendSelected.codeId, amendSelected.siteId]);
  const info = () => {
    alert(
      isPartner
        ? "Update Stock Here\n\nWhy? So your main user knows when to reorder and avoid running out.\n\nWhat you can do:\n• Only use 'Update' tab to update stock\n• Your update goes to main user instantly\n• Use the slider for fast entry\n\nGoal: Never run out of material!"
        : "Manage Stock & Partners\n\nWhy? To prevent production stoppages by tracking stock in real time.\n\nYour Tools:\n• Report → Enter stock first time\n• Update → Fix or update stock\n• Assign → Give partners access to sites\n\nGoal: Always have enough stock, on time!"
    )
  }

  if (loading || accountLoading || partnerLoading || !mainUserId) {
    return <div className="stockstatus_loader">Loading...</div>
  }

  const capacityInfo = () => {
    alert("This is the total capacity of the godown. Enter the maximum storage capacity here.")
  }

  const currentStockInfo = () => {
    alert("Current stock is the actual stock at the site at the present time, which should be updated by the site person on a daily basis.")
  }
  const filteredSites = getFilteredSites()
  return (
    <div className="stockstatus_page">
      <div className="stockstatus_card">
        <header className="stockstatus_header">
          <h1>
            Report Your Stock
            <img src="/Images/stock.png" className="stock-img-icon" />
          </h1>
          <MdInfo onClick={info} className="stockstatus_info_icon" />
        </header>

        <div className="stockstatus_tabs">


          <button
            className={activeTab === "amend" ? "stockstatus_tab active" : "stockstatus_tab"}
            onClick={() => setActiveTab("amend")}
          >
            <p><FaEdit /> Check & Update
              <MdInfo
                className="stockstatus_tab_info ml-2"
                onClick={(e) => {
                  e.stopPropagation()
                  alert(
                    "Check & Update Stock\n\n" +
                    "• Select any site (new or already reported)\n" +
                    "• For new sites: enter capacity + current stock\n" +
                    "• For existing sites: update current stock\n" +
                    "• Use slider for quick update\n" +
                    "Goal: Keep stock levels accurate and get timely alerts for uninterrupted supply."
                  )
                }}
              />
            </p>
          </button>

          {!isPartner && (
            <button
              className={activeTab === "assign" ? "stockstatus_tab active" : "stockstatus_tab"}
              onClick={() => setActiveTab("assign")}
            >
              <p>
                <FaUserTie /> Assign Partner
                <MdInfo
                  className="stockstatus_tab_info ml-2"
                  onClick={(e) => {
                    e.stopPropagation()
                    alert(
                      "Assign Partner Tab\n\nUse this to give a partner permission to update stock for specific sites.\n\n• Only main users can assign\n• Partners will then see and update only their assigned sites"
                    )
                  }}
                />
              </p>
            </button>
          )}
        </div>

        {/* AMEND TAB */}
        {activeTab === "amend" && (
          <div className="stockstatus_tab_pane">
            <div className="stockstatus_dropdown_group">
              <label>Select Site to Update</label>
             <div
  className="stockstatus_dropdown"
  onClick={() => setAmendDropdownOpen((prev) => !prev)}
>
  <span>
    {amendSelected.codeId && amendSelected.siteId
      ? formatFullAddress(
          codes[amendSelected.codeId]?.sites?.[amendSelected.siteId]
        )
      : "Select Site to Update"}
  </span>

  <FaChevronDown
    className={
      amendDropdownOpen
        ? "stockstatus_arrow open"
        : "stockstatus_arrow"
    }
  />
</div>

              {amendDropdownOpen && (
                <ul className="stockstatus_menu">
                  {filteredSites.map((site) => (
                    <li
                      key={`${site.codeId}_${site.siteId}`}
                      onClick={() => {
                        setAmendSelected({
                          codeId: site.codeId,
                          siteId: site.siteId,
                          unit: site.stockUnit || "MT",
                        })
                        setAmendCapacity(site.stockCapacity || "")
                        setAmendCurrent(site.currentStock || "")
                        setAmendDropdownOpen(false)
                      }}
                    >
                      {formatFullAddress(site)}
                      {!site.stockCapacity && (
                        <span style={{ color: "#e67e22", fontSize: "0.85em" }}>  (New site)</span>
                      )}
                    </li>
                  ))}

                  {filteredSites.length === 0 && (
                    <li className="stockstatus_no_data">No sites available yet</li>
                  )}
                </ul>
              )}
            </div>

            {(() => {
              const allSites = getFilteredSites();

              if (loading) {
                return <div className="stockstatus_loader">Loading sites...</div>;
              }

              if (allSites.length === 0) {
                return (
                  <div className="stockstatus_no_data" style={{ marginTop: '2rem', textAlign: 'center' }}>
                    No sites available yet
                  </div>
                );
              }
              // Sites exist → show the form only if something is selected
              return (
                <>
                  {amendSelected.siteId ? (
                    <>
                      {/* Capacity - readonly + Set Qty button */}
                      <div className="stockstatus_input_row">
                        <div className="stockstatus_input_box">

                          <label className="stockstatus_label_with_dropdown">
                            Godown Capacity (Total)
                            <select
                              value={amendSelected.unit}
                              onChange={(e) =>
                                setAmendSelected((prev) => ({ ...prev, unit: e.target.value }))
                              }
                              className="stockstatus_label_dropdown"
                            >
                              <option value="MT">MT</option>
                              <option value="Bags">Bags</option>
                            </select>
                            <MdInfo
                              className="md-icons"
                              onClick={capacityInfo}
                            />
                          </label>

                          <div className="capacity-with-button">
                            <button
                              type="button"
                              className="set-qty-btn"
                              onClick={() => setShowSetCapacityModal(true)}
                            >
                              Set Qty
                            </button>
                            <input
                              type="text"
                              value={amendCapacity}
                              readOnly
                              className="readonly-input"
                              placeholder="Click 'Set Qty' to change"
                            />

                          </div>
                        </div>
                      </div>

                      {/* Cylinder and Slider */}
                      <div className="stockstatus_visual_row">
                        <div className="stockstatus_cylinder_container">

                          {/* Left labels – absolute positioned */}
                          <div className="cylinder-scale-labels">
                            <div className="scale-label top">Full</div>
                            <div className="scale-label middle">Half</div>
                            <div className="scale-label bottom">Empty</div>
                          </div>

                          {/* Cylinder + message */}
                          <div className="cylinder-with-message">
                            <div className="stockstatus_cylinder">
                              <div
                                className={`stockstatus_fill ${getFillClass(amendLivePercentage)}`}
                                style={{ height: `${amendLivePercentage || 0}%` }}
                              ></div>
                            </div>
                            <div className="stockstatus_msg">
                              {getMessage(amendLivePercentage).icon} <span>{getMessage(amendLivePercentage).msg}</span>
                            </div>
                          </div>

                        </div>
                      </div>
                      {/* SLIDER */}
                      <div className="stockstatus_slider_container">
                        <input
                          type="range"
                          min="0"
                          max={amendCapacity || 100}
                          value={amendCurrent || 0}
                          onChange={(e) => setAmendCurrent(e.target.value)}
                          className="stockstatus_slider"
                          style={{
                            background: `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${amendLivePercentage || 0}%, #e5e7eb ${amendLivePercentage || 0}%, #e5e7eb 100%)`,
                          }}
                        />
                        {/* SLIDER LABELS – AMEND */}
                        <div className="stockstatus_slider_labels">
                          <span className="stockstatus_slider_label_left"></span>

                          <span className="stockstatus_slider_mid">
                            {amendCurrent ? (
                              <strong>{amendCurrent} {amendSelected.unit}</strong>
                            ) : (
                              "—"
                            )}
                          </span>

                          <span className="stockstatus_slider_label_right">
                            {amendCapacity ? `${amendCapacity} ${amendSelected.unit}` : "?"}
                          </span>
                        </div>
                      </div>
                      {/* Current Stock */}
                      <div className="stockstatus_input_row">
                        <div className="stockstatus_input_box">
                          {amendSelected.siteId && codes[amendSelected.codeId]?.sites?.[amendSelected.siteId]?.updatedOn && (
                            <div className="last-updated-info">
                              Last Updated On: <span style={{ color: '#ea8716' }}> {getIndianTime(codes[amendSelected.codeId].sites[amendSelected.siteId].updatedOn)}</span>
                            </div>
                          )}
                          <label className="stockstatus_label_with_dropdown">
                            Current Stock
                            <select
                              value={amendSelected.unit}
                              onChange={(e) =>
                                setAmendSelected((prev) => ({ ...prev, unit: e.target.value }))
                              }
                              className="stockstatus_label_dropdown"
                            >
                              <option value="MT">MT</option>
                              <option value="Bags">Bags</option>
                            </select>
                            <MdInfo
                              className="md-icons"
                              onClick={currentStockInfo}
                            />
                          </label>

                          <input
                            type="number"
                            min="0"
                            step="any"
                            value={amendCurrent}
                            onChange={(e) => {
                              const val = e.target.value;

                              // Allow empty input (user clearing field)
                              if (val === "") {
                                setAmendCurrent("");
                                setCurrentStockError("");
                                return;
                              }

                              const num = Number(val);

                              // Block non-numeric or negative
                              if (isNaN(num) || num < 0) return;

                              // NEW: Check against capacity
                              const cap = Number(amendCapacity);
                              if (!isNaN(cap) && cap > 0 && num > cap) {
                                setCurrentStockError(`Current stock cannot exceed Godown Capacity (${cap} ${amendSelected.unit})`);
                                // Do NOT update state — prevent invalid value
                                return;
                              }

                              // If valid → clear error and update
                              setCurrentStockError("");
                              setAmendCurrent(num);
                            }}
                            onWheel={(e) => e.target.blur()}
                            placeholder={`Current stock (${amendSelected.unit}) - max ${amendCapacity || "—"}`}
                            className={`current-stock-input ${currentStockError ? "input-error" : ""}`}
                            max={amendCapacity || undefined} // HTML max attribute (extra safety)
                          />
                          {currentStockError && (
                            <p className="stockstatus_error_message" style={{ color: "#d32f2f", fontSize: "0.85rem", marginTop: "6px" }}>
                              {currentStockError}
                            </p>
                          )}
                        </div>
                      </div>
                      {amendSelected.siteId && !codes[amendSelected.codeId]?.sites?.[amendSelected.siteId]?.stockCapacity && (
                        <div style={{
                          color: "#e67e22",
                          fontWeight: "500",
                          margin: "1rem 0",
                          textAlign: "center"
                        }}>
                          This is the first time reporting stock for this site.
                        </div>
                      )}
                      {/* Update button */}
                      <div className="stockstatus_btn_container">
                        <button
                          onClick={() => setShowAmendConfirm(true)}
                          disabled={amending || !amendCapacity || !amendCurrent ||
                            Number(amendCurrent) > Number(amendCapacity) ||   // ← NEW
                            !!currentStockError
                          }
                          className="stockstatus_btn_primary"
                        >
                          {amending
                            ? "Saving..."
                            : !codes[amendSelected.codeId]?.sites?.[amendSelected.siteId]?.stockCapacity
                              ? "Add Stock (First Time)"
                              : "Update Stock"}
                        </button>
                      </div>
                    </>
                  ) : (
                    // This case should almost never happen because of auto-select,
                    // but good fallback
                    <div className="stockstatus_empty_msg">
                      Select a site to update stock
                    </div>
                  )}

                  {/* ─── Set Capacity Modal ─── */}
                  {showSetCapacityModal && (
                    <div className="stockstatus_modal_overlay">
                      <div className="stockstatus_modal_box">
                        <h3>Set Godown Capacity</h3>
                        <p>Max allowed: 10 crore (100000000 {amendSelected.unit})</p>

                        <input
                          type="number"
                          min="0"
                          step="any"
                          value={tempCapacity}
                          onChange={(e) => {
                            const val = e.target.value
                            if (val === "") {
                              setTempCapacity("")
                              return
                            }
                            const num = Number(val)
                            if (!isNaN(num) && num <= MAX) {
                              setTempCapacity(num)
                            }
                          }}
                          placeholder="e.g. 50000"
                          autoFocus
                          className="godwon-input"
                          onWheel={(e) => e.target.blur()}
                        />

                        <div className="stockstatus_modal_buttons">
                          <button
                            className="confirm_btn"
                            disabled={!tempCapacity || Number(tempCapacity) > MAX}
                            onClick={() => {
                              setAmendCapacity(Number(tempCapacity))
                              setTempCapacity("")
                              setShowSetCapacityModal(false)
                              alert(
                                "You have set your total godown capacity.\nNow you can update your current stock."
                              )
                            }}
                          >
                            Yes, Update
                          </button>
                          <button
                            className="cancel_btn"
                            onClick={() => {
                              setTempCapacity("")
                              setShowSetCapacityModal(false)
                            }}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* ─── Amend Confirm Modal ─── */}
                  {showAmendConfirm && (
                    <div className="stockstatus_modal_overlay">
                      <div className="stockstatus_modal_box">
                        <h3>Confirm Stock Update</h3>
                        <p>
                          Total Godown Capacity: {amendCapacity} {amendSelected.unit}
                          <br />
                          Current Stock: {amendCurrent} {amendSelected.unit} ({amendLivePercentage}%)
                        </p>
                        <div className="stockstatus_modal_buttons">
                          <button
                            className="confirm_btn"
                            onClick={async () => {
                              setShowAmendConfirm(false)
                              await handleSaveStock()
                            }}
                          >
                            Yes
                          </button>
                          <button className="cancel_btn" onClick={() => setShowAmendConfirm(false)}>
                            No
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        )}

        {activeTab === "assign" && !isPartner && (
          <div className="stockstatus_tab_pane">
            <div className="stockstatus_assign_container">
              <h3>Assign Partner to Sites</h3>
              <p className="text-center">Select sites and assign a partner to manage them</p>

              {filteredSites.filter(s => s.stockCapacity).length === 0 ? (
                <p className="stockstatus_empty_msg">
                  No sites with reported stock yet. Report stock first to assign partners.
                </p>
              ) : (
                <div className="stockstatus_sites_list">
                  {filteredSites
                    .filter((s) => s.stockCapacity)  // ← THIS LINE ADDED
                    .map((s, idx) => {
                      const siteKey = `${s.codeId}_${s.siteId}`
                      const assignedPartner = sitePartnerAssignments[siteKey]
                      return (
                        <div key={idx} className="stockstatus_site_card">
                          <div className="stockstatus_site_info">
                            <p className="stockstatus_site_full">
                              {formatFullAddress(s)}
                            </p>
                            {assignedPartner && (
                              <p className="stockstatus_assigned">
                                Assigned to: <strong>{assignedPartner.partnerName}</strong>
                              </p>
                            )}
                          </div>
                          <button
                            onClick={() => {
                              setAssigningToSite({ ...s, codeId: s.codeId, siteId: s.siteId })
                              setSelectedPartner(assignedPartner?.partnerId || "")
                              setShowAssignModal(true)
                            }}
                            className="stockstatus_assign_btn"
                          >
                            <FaUserTie /> {assignedPartner ? "Change Partner" : "Assign Partner"}
                          </button>
                        </div>
                      )
                    })}
                </div>
              )}
            </div>

            {showAssignModal && (
              <div className="stockstatus_modal_overlay">
                <div className="stockstatus_modal_box stockstatus_assignment_modal">
                  <h3>Assign Partner to Site</h3>
                  <p className="stockstatus_modal_site">
                    <strong>Site:</strong> {assigningToSite ? formatFullAddress(assigningToSite) : ""}
                  </p>

                  <div className="stockstatus_modal_form">
                    <label>Select Partner:</label>
                    <select
                      value={selectedPartner}
                      onChange={(e) => setSelectedPartner(e.target.value)}
                      className="stockstatus_modal_select"
                    >
                      <option value="">-- Choose Partner --</option>
                      {partners.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="stockstatus_modal_buttons">
                    <button className="confirm_btn" onClick={handleAssignPartner} disabled={!selectedPartner}>
                      Assign
                    </button>
                    <button
                      className="cancel_btn"
                      onClick={() => {
                        setShowAssignModal(false)
                        setAssigningToSite(null)
                        setSelectedPartner("")
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}


      </div>
    </div>
  )
}

export default UserStockStatus